import type { CardParagraphAnchor, RedlineTextInsertion, RedlineTextProposal } from "@common/utils/redline/wire/redline-payload";
import type { RedlinePlacement } from "@common/utils/redline/wire/redline-placement";

import { getActionIdentifier, isApplicableActionMessage } from "../../../types/chat";
import type { ActionFailure, ChatMessage } from "../../../types/chat";
import type { LiveProposalState } from "../../../utils/anchor/proposal/live-proposal";
import type { RecoveryAttempt } from "../../../utils/anchor/recovery/recovery-chain";
import type { CommentCardContent, ProposalCardContent, ReplyCardContent } from "./card-content";
import { PROPOSAL_CARD_KIND_LABELS, proposalCardKindOfContent } from "./card-kinds";
import type { LastApplyResult } from "./last-apply-results";

export const ANCHOR_NOT_STORED = "not stored (card from before today)";
export const NOT_RECORDED = "not recorded";

export interface CardDetailsIds {
  cardId: string;
  cardNumber: number | null;
  conversationId: string | null;
  messageId: string | null;
  toolCallId: string | null;
}

export interface CardDetailsRecordedOutcome {
  applied: boolean;
  appliedAt: string | null;
  dismissed: boolean;
  refused: boolean;
  failure: ActionFailure | null;
  supersededBy: string | null;
  partSupersededBy: string | null;
  recoveryWarning: string | null;
}

export interface CardDetailsTracker {
  placement: RedlinePlacement;
  lastAttempt: RecoveryAttempt | null;
}

export interface CardDetailsLive {
  status: LiveProposalState["status"];
  changeCount: number;
  error: string | null;
  stampedAt: string | null;
  placement: RedlinePlacement;
  lastSource: string | null;
  lastUpdatedAtMs: number | null;
  previewPackageLength: number;
}

export interface CardDetailsSource {
  ids: CardDetailsIds;
  content: ProposalCardContent | null;
  textRedlineBeforeEdit: RedlineTextProposal | null;
  recorded: CardDetailsRecordedOutcome;
  live: LiveProposalState | null;
  tracker: CardDetailsTracker | null;
  lastApply: LastApplyResult | null;
}

export interface CardDetailsParagraph {
  bookmark: string | null;
  textWhenProposed: string;
  textProposed: string;
  anchor: CardParagraphAnchor | null;
}

export interface CardDetailsIdentity extends CardDetailsIds {
  kind: string | null;
  author: string | null;
  stampedAt: string | null;
}

export interface CardDetails {
  identity: CardDetailsIdentity;
  currentStatus: string;
  recorded: CardDetailsRecordedOutcome;
  live: CardDetailsLive | null;
  bookmarks: string[];
  afterBookmarks: string[];
  paragraphs: CardDetailsParagraph[];
  insertions: RedlineTextInsertion[];
  tableChanges: RedlineTextProposal["tableChanges"] | null;
  reverts: RedlineTextProposal["reverts"] | null;
  comment: CommentCardContent | null;
  reply: ReplyCardContent | null;
  userEditBaseline: RedlineTextProposal | null;
  tracker: CardDetailsTracker | null;
  lastApply: LastApplyResult | null;
  raw: Record<string, unknown>;
}

export function cardMessageOf(cardMessages: ChatMessage[], cardId: string): ChatMessage | null {
  return cardMessages.find((candidate) => isApplicableActionMessage(candidate) && getActionIdentifier(candidate, "") === cardId) ?? null;
}

export function textRedlineOfCardContent(content: ProposalCardContent | null): RedlineTextProposal | null {
  if (content === null) return null;
  return content.kind === "redline" || content.kind === "table" || content.kind === "paragraph" ? content.textRedline : null;
}

export function bookmarksOfCardContent(content: ProposalCardContent | null): string[] {
  if (content === null) return [];
  if (content.kind === "paragraph") return content.bookmarks ?? [content.precedingParagraphBookmark];
  if (content.kind === "reply") return [];
  return content.bookmarks;
}

function afterBookmarksOfCardContent(content: ProposalCardContent | null): string[] {
  if (content === null || content.kind === "comment" || content.kind === "reply") return [];
  return content.afterBookmarks ?? [];
}

function currentStatusOf(recorded: CardDetailsRecordedOutcome, live: LiveProposalState | null, lastApply: LastApplyResult | null): string {
  if (recorded.dismissed) return "dismissed";
  if (recorded.failure !== null) return recorded.failure.isPartiallyApplied ? "partly applied" : "failed";
  if (lastApply !== null && lastApply.outcome === "partly_applied") return "partly applied";
  if (recorded.refused) return "refused";
  if (recorded.applied) return "applied";
  if (recorded.supersededBy !== null) return "superseded";
  if (live !== null && live.status !== "off") return live.status;
  return "pending";
}

function liveSummaryOf(live: LiveProposalState | null): CardDetailsLive | null {
  if (live === null) return null;
  return {
    status: live.status,
    changeCount: live.changeCount,
    error: live.error,
    stampedAt: live.stampedAt,
    placement: live.placement,
    lastSource: live.lastSource === null ? null : String(live.lastSource),
    lastUpdatedAtMs: live.lastUpdatedAtMs,
    previewPackageLength: live.previewPackageOoxml?.length ?? 0,
  };
}

export function cardDetailsOf(source: CardDetailsSource): CardDetails {
  const textRedline = textRedlineOfCardContent(source.content);
  const kind = source.content === null ? null : PROPOSAL_CARD_KIND_LABELS[proposalCardKindOfContent(source.content)];
  const live = liveSummaryOf(source.live);
  const identity: CardDetailsIdentity = {
    ...source.ids,
    kind,
    author: textRedline?.author ?? null,
    stampedAt: source.live?.stampedAt ?? null,
  };
  const paragraphs: CardDetailsParagraph[] = (textRedline?.paragraphs ?? []).map((paragraph) => ({
    bookmark: paragraph.bookmark,
    textWhenProposed: paragraph.textWhenProposed,
    textProposed: paragraph.textProposed,
    anchor: paragraph.anchor ?? null,
  }));
  const details: Omit<CardDetails, "raw"> = {
    identity,
    currentStatus: currentStatusOf(source.recorded, source.live, source.lastApply),
    recorded: source.recorded,
    live,
    bookmarks: bookmarksOfCardContent(source.content),
    afterBookmarks: afterBookmarksOfCardContent(source.content),
    paragraphs,
    insertions: textRedline?.insertions ?? [],
    tableChanges: textRedline?.tableChanges ?? null,
    reverts: textRedline?.reverts ?? null,
    comment: source.content?.kind === "comment" ? source.content : null,
    reply: source.content?.kind === "reply" ? source.content : null,
    userEditBaseline: source.textRedlineBeforeEdit,
    tracker: source.tracker,
    lastApply: source.lastApply,
  };
  return { ...details, raw: { ...details } };
}

export function cardDetailsJsonOf(details: CardDetails): string {
  return JSON.stringify(details.raw, null, 2);
}
